import React, { useState } from 'react';
import './Spark.css';

// THE SPARK — one small chart, one colour (the layer's hue, or `hue`): the
// shape of a count over time, not figures to read. Two forms:
//
//   · line — a quiet line of its values (an app's days of use); `tall` lays a
//            wash under it
//   · bars — one bar per stretch of time (the Activity log's pulse); a stretch
//            with nothing is a hairline
//
// Hovering reads one value out in the head (`readout(i)`; a falsy word shows
// nothing), the others quieten. `title` heads it; `ends` names the two ends
// under it, or `ticks(i)` names some of the bars.
//
//   <Spark values={days} readout={(i) => `${when(i)} · ${days[i]}`} ends={['1 Sep', 'Today']} tall />
//   <Spark form="bars" values={bins} title="This week" readout={…} ticks={(i) => …} />
//
// The graph's timeline (FilterRail) is not one: it is dragged across to pick a range.

const W = 100;
const H = 24;

export default function Spark({ values, form = 'line', hue, tall = false, title, readout, ends, ticks, className = '' }) {
    const [hover, setHover] = useState(null);
    const n = values.length;
    const peak = Math.max(1, ...values);
    const quiet = !values.some(Boolean);
    const word = hover != null && readout ? readout(hover) : null;
    const head = (title || readout) && (
        <div className="nx-spark__head">
            {title && <span className="nx-spark__title">{title}</span>}
            <span className="nx-spark__readout">{word || ' '}</span>
        </div>
    );
    const foot = ticks
        ? <div className="nx-spark__foot is-ticks" aria-hidden="true">{values.map((_, i) => <span key={i}>{ticks(i) || ''}</span>)}</div>
        : ends && <div className="nx-spark__foot" aria-hidden="true"><span>{ends[0]}</span><span>{ends[1]}</span></div>;
    const cls = `nx-spark is-${form}${tall ? ' is-tall' : ''}${quiet ? ' is-quiet' : ''}${className ? ` ${className}` : ''}`;
    const style = hue ? { '--spark-hue': hue } : undefined;

    if (form === 'bars') {
        return (
            <div className={cls} style={style}>
                {head}
                <div className="nx-spark__plot" onMouseLeave={() => setHover(null)}>
                    {values.map((v, i) => (
                        <div key={i} className={`nx-spark__col${hover != null && hover !== i ? ' is-quiet' : ''}`} onMouseEnter={readout ? () => setHover(i) : undefined}>
                            {v === 0 ? <span className="nx-spark__nil" /> : <span className="nx-spark__bar" style={{ height: `max(3px, ${(v / peak) * 100}%)` }} />}
                        </div>
                    ))}
                </div>
                {foot}
            </div>
        );
    }

    const x = (i) => (n > 1 ? (i / (n - 1)) * W : W / 2);
    const y = (v) => H - 1 - (v / peak) * (H - 2);
    const points = values.map((v, i) => `${x(i).toFixed(2)},${y(v).toFixed(2)}`).join(' ');
    const track = readout ? (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setHover(Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left) / r.width) * (n - 1)))));
    } : undefined;
    return (
        <div className={cls} style={style}>
            {head}
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true" onMouseMove={track} onMouseLeave={readout ? () => setHover(null) : undefined}>
                {tall && !quiet && <polygon className="nx-spark__area" points={`0,${H} ${points} ${W},${H}`} />}
                <polyline className="nx-spark__stroke" points={points} vectorEffect="non-scaling-stroke" />
                {hover != null && <line className="nx-spark__at" x1={x(hover)} x2={x(hover)} y1="0" y2={H} vectorEffect="non-scaling-stroke" />}
            </svg>
            {foot}
        </div>
    );
}
